import React, { useState, useEffect, useCallback } from "react";

/* ── PWA install prompt (v5.5) ────────────────────────────────────────────────
 * A dismissible banner that offers one-tap install on browsers that support the
 * beforeinstallprompt event (Android Chrome/Edge and similar), and falls back to
 * brief "Add to Home Screen" instructions on iOS (which has no programmatic
 * install API). Shows nothing when the app is already installed, already
 * dismissed, or install isn't possible.
 *
 * Used in two places (Landing pre-login, Ironlifts shell post-login) via the
 * shared useInstallPrompt hook. A single localStorage flag is shared between
 * them so dismissing in one place suppresses both — no double-nagging.
 */

const DISMISS_KEY = "llh_install_dismissed";

// Capture the beforeinstallprompt event at MODULE LOAD, not inside a component.
// The browser can fire it before React mounts; if we only listened inside a
// component's effect we could miss it. We stash it here and notify any mounted
// hook via a tiny subscriber list.
let deferredPrompt = null;
let installed = false;
const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();        // suppress the browser's default mini-infobar
    deferredPrompt = e;        // stash for our own button
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true   // iOS Safari installed flag
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPhone/iPod/iPad, plus iPadOS 13+ which reports as Mac but is touch-capable.
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS13 = ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document;
  return iOSDevice || iPadOS13;
}

function dismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === "1"; }
  catch (e) { return false; }
}

/* Returns the state + actions a banner needs. `mode` is:
 *   'prompt'       — real one-tap install available (deferredPrompt captured)
 *   'ios'          — iOS, show manual Add-to-Home-Screen instructions
 *   null           — show nothing (installed, dismissed, standalone, or the
 *                     browser hasn't (yet) signalled installability on non-iOS)
 */
export function useInstallPrompt() {
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);

  useEffect(() => {
    subscribers.add(rerender);
    return () => { subscribers.delete(rerender); };
  }, [rerender]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch (e) { /* ignore */ }
    // prompt() is single-use; drop it regardless of outcome. If the user
    // dismissed the native dialog, the browser may fire beforeinstallprompt
    // again later, which re-captures a fresh event.
    deferredPrompt = null;
    notify();
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* ignore */ }
    rerender();
  }, [rerender]);

  // Decide what (if anything) to show.
  let mode = null;
  if (!isStandalone() && !installed && !dismissed()) {
    if (deferredPrompt) mode = "prompt";
    else if (isIOS()) mode = "ios";
  }

  return { mode, install, dismiss };
}

/* The banner UI. Renders nothing unless there's something to show. Visual
 * language matches the v5 push-permission card (raised surface, brass accent,
 * Cinzel eyebrow). `variant` controls placement styling:
 *   'floating' — fixed bottom sheet (used in the app shell, like the push card)
 *   'inline'   — sits in normal document flow (used on the Landing page)
 */
export function InstallBanner({ variant = "floating" }) {
  const { mode, install, dismiss } = useInstallPrompt();
  if (!mode) return null;

  const inner = (
    <div className="llh-install-card">
      <div className="llh-install-eyebrow">Install Lift'n Hour</div>
      {mode === "prompt" ? (
        <>
          <div className="llh-body-text">
            Add Lift'n Hour to your home screen for one-tap access and reliable
            rest-timer alerts.
          </div>
          <div className="llh-actions-end">
            <button onClick={dismiss} className="llh-install-btn-ghost">Not now</button>
            <button onClick={install} className="llh-install-btn-brass">Install</button>
          </div>
        </>
      ) : (
        <>
          <div className="llh-body-text">
            To install on iPhone or iPad: tap the Share button, then choose
            “Add to Home Screen.”
          </div>
          <div className="llh-actions-end">
            <button onClick={dismiss} className="llh-install-btn-ghost">Got it</button>
          </div>
        </>
      )}
    </div>
  );

  if (variant === "inline") {
    return <div className="llh-install-inline">{inner}</div>;
  }
  // floating (default): fixed bottom sheet, matches the push-permission card
  return (
    <div className="llh-install-floating">
      {inner}
    </div>
  );
}
